import {observer} from 'mobx-react-lite';
import {
    Header,
    IButtonProps,
    IDatepickerRangeProps,
    IFilterItemProps,
    IFilterProps,
    IPageSelectProps,
    ISidemenuProps,
} from '../../../patterns';
import React, {useContext, useState} from 'react';
import {routes} from './routes';
import {RootStore, StoreContext} from './root-store';
import {Route, RouterStore} from 'mobx-router';
import {INavigationItemProps} from '../../../patterns/components/navigation/navigation';
import {Language} from './label-store';
import {AnswerContext, Channel, DeliveryTime, QuestionsFrequency, UserStore} from '../user/user-store';
import {Capacitor} from '@capacitor/core';
import {DateRange} from 'react-day-picker';
import {formatToDayAndMonthWithDateInput, getLocaleFromLanguage, isVacationStartInFuture} from './dateUtils';

const menuNavigationItem = (router: RouterStore<RootStore>, route: Route<RootStore>, label: string, icon: string, count: number | undefined): INavigationItemProps => {
    let activeRoute = router.currentRoute;

    if (router.currentRoute === routes.home) {
        if (Capacitor.getPlatform() === 'web') {
            activeRoute = routes.dashboard;
        } else {
            activeRoute = routes.chat;
        }
    }
    if (router.currentRoute && [routes.chatHistory, routes.chatAskQuestion, routes.chatAnswerQuestion, routes.chatChooseLanguage].includes(router.currentRoute)) {
        activeRoute = routes.chat;
    }
    if (router.currentRoute === routes.fromEmailToDashboard) {
        activeRoute = routes.dashboard;
    }

    return ({
        url: (): Promise<void> => router.goTo(route),
        label,
        icon,
        count,
        current: activeRoute === route,
    });
};

export const WebappHeader = observer((): JSX.Element => {
    const { userStore, router, labelStore, sessionStore } = useContext(StoreContext);
    const existingVacationPeriod = userStore.payload?.vacationTime;
    const [timeZone, setTimeZone] = useState<string>(userStore.payload?.employeeTimeZone || '');

    const existingVacation = existingVacationPeriod === undefined ? undefined : {
        from: new Date(existingVacationPeriod.from),
        to: new Date(existingVacationPeriod.to),
    } as DateRange;

    const [vacationRange, setVacationRange] = useState<Date | DateRange | undefined>(existingVacation);

    if (userStore.state !== 'user-loaded') {
        return <React.Fragment/>;
    }

    const logo = userStore.payload!.logoFile;

    const navigationMenuItems: INavigationItemProps[] = [];

    if (userStore.payload?.isManagerOrPartner === true || userStore.payload?.isAdmin === true) {
        navigationMenuItems.push(
            menuNavigationItem(router, routes.chat, labelStore.translate('chat.my-answers.title'), 'message', undefined),
            menuNavigationItem(router, routes.dashboard, labelStore.translate('dashboard.title'), 'bar-graph', undefined),
        );
        if (userStore.payload?.isMentorEnabled === true) {
            navigationMenuItems.push(menuNavigationItem(router, routes.mentorHistory , labelStore.translate('mentor.title'), 'robot-happy', undefined));
        }
        navigationMenuItems.push(
            menuNavigationItem(router, routes.questions, labelStore.translate('questions.title'), 'messages', undefined),
            menuNavigationItem(router, routes.company, labelStore.translate('company.title'), 'users', undefined),
        );
    } else {
        navigationMenuItems.push(menuNavigationItem(router, routes.chat, labelStore.translate('chat.my-answers.title'), 'message', undefined),
            menuNavigationItem(router, routes.dashboard, labelStore.translate('dashboard.title'), 'bar-graph', undefined));
    }

    const companyLanguages = userStore.payload!.companyLanguages;

    const languagesProps: IFilterProps | undefined = companyLanguages !== undefined && companyLanguages.length > 1 ? {
        label: labelStore.translate('common.language'),
        value: userStore.payload!.language,
        items: companyLanguages.map((language): IFilterItemProps => ({
            id: language,
            name: 'language',
            label: labelStore.translate(`common.language.${language}`),
            value: language,
            onChange: (): Promise<void> => userStore.changeLanguage(language as Language),
        })),
    } : undefined;

    const scheduleQuestionsSendTimeProps: IFilterProps = {
        label: labelStore.translate('user-menu.delivery-time'),
        value: userStore.payload!.deliveryTime,
        items: ['morning', 'afternoon', 'evening', 'latenight'].map((deliveryTime): IFilterItemProps => ({
            id: deliveryTime,
            name: 'schedule',
            label: labelStore.translate(`user-menu.delivery-time.${deliveryTime}`),
            value: deliveryTime,
            onChange: (): Promise<void> => userStore.changeDeliveryTime(deliveryTime as DeliveryTime),
        })),
    };

    const questionsFrequencyProps: IFilterProps = {
        label: labelStore.translate('user-menu.questions-frequency'),
        value: userStore.payload!.employeeQuestionsFrequency,
        items: ['one_every_day', 'weekly'].map((frequency): IFilterItemProps => ({
            id: frequency,
            name: 'frequency',
            label: labelStore.translate(`common.questions-frequency.${frequency}`),
            value: frequency,
            onChange: (): Promise<void> => userStore.changeQuestionsFrequency(frequency as QuestionsFrequency),
        })),
    };

    const answerContextProps: IFilterProps = {
        label: labelStore.translate('user-menu.answer-context'),
        value: userStore.payload!.employeeAnswerContext,
        items: userStore.payload!.companyAnswerContext.map((scale): IFilterItemProps => ({
            id: scale,
            name: 'scale',
            label: labelStore.translate(`common.answer-context.${scale}`),
            value: scale,
            onChange: (): Promise<void> => userStore.changeAnswerContext(scale as AnswerContext),
        })),
    };

    const eligibleUserChannels = userStore.payload?.eligibleChannelsForUserMenu;

    const channelProps: IFilterProps = {
        label: labelStore.translate('common.channel.title'),
        value: userStore.payload!.channel,
        items: eligibleUserChannels!.map((channel): IFilterItemProps => ({
            id: channel,
            name: 'channel',
            label: labelStore.translate(`common.channel.${channel}`),
            value: channel,
            onChange: (): Promise<void> => userStore.initiateChannelChange(channel as Channel),
        })),
    };

    const isUserActiveProps: IFilterProps = {
        label: labelStore.translate('user-menu.user-status.title'),
        value: existingVacation && isVacationStartInFuture(existingVacation.from) === false ? 'passive' : 'active',
        items: [({
            id: 'active',
            name: 'active',
            label: labelStore.translate('user-menu.user-status.active'),
            value: 'active',
            onChange: (): void => {
                userStore.cancelScheduledVacation();
                setVacationRange(undefined);
            },
        }),
        ({
            id: 'passive',
            name: 'passive',
            label: labelStore.translate('user-menu.user-status.passive'),
            value: 'passive',
            onChange: (): void => userStore.openVacationCalendar(),
        })],
    };

    const timezoneDropdownProps = (store: UserStore): IPageSelectProps | undefined => {
        const options = userStore.payload?.timezoneId.map((timezone) => ({
            label: timezone,
            value: timezone,
        }));

        return {
            label: labelStore.translate('user-menu.select-timezone'),
            id: 'dashboard-timezone-filter',
            value: timeZone,
            options,
            onChange: (selectedOption: string): void => {
                setTimeZone(selectedOption);
                userStore.changeTimeZone(selectedOption, store.payload?.employeeId);
            },
        } as IPageSelectProps;
    };

    const vacationPlannerProps: IDatepickerRangeProps = {
        format: 'dd.MM.yyyy',
        type: 'range',
        field: [
            {
                label: labelStore.translate('user-menu.datepicker.from'),
                id: 'datepicker-from',
                name: 'datepicker-from',
            },
            {
                label: labelStore.translate('user-menu.datepicker.to'),
                id: 'datepicker-to',
                name: 'datepicker-to',
            },
        ],
        value: vacationRange as DateRange,
        onDateChange: (value: DateRange | Date | undefined): void => {
            setVacationRange(value as DateRange);
            userStore.scheduleVacation(value as DateRange);
            if ((value as DateRange).from !== null && (value as DateRange).from !== undefined && (value as DateRange).to !== null && (value as DateRange).to !== undefined){
                if (isVacationStartInFuture((value as DateRange).from) === true) {
                    userStore.isCalendarOpen = false;
                    setVacationRange(undefined);
                }
            }
        },
        locale: getLocaleFromLanguage(userStore.payload?.language),
    };

    const cancelVacationButtonProps = (): IButtonProps => ({
        text: labelStore.translate('user-menu.cancel-vacation-button'),
        onClick: (): void => {
            userStore.cancelScheduledVacation();
            setVacationRange(undefined);
        },
    });

    const futureVacationRangeProps = (): string | undefined => {
        if (!userStore.isCalendarOpen || existingVacationPeriod === undefined) {
            return undefined;
        } else {
            const isStarDateInFuture = isVacationStartInFuture(new Date(existingVacationPeriod?.from));
            const daterange: string = formatToDayAndMonthWithDateInput(new Date(existingVacationPeriod!.from)) + ' - ' + formatToDayAndMonthWithDateInput(new Date(existingVacationPeriod!.to));

            return isStarDateInFuture === true ? daterange : undefined;
        }
    };

    const cancelFutureVacationProps = (): IButtonProps | undefined => {
        if (!userStore.isCalendarOpen || existingVacationPeriod === undefined) {
            return undefined;
        } else {
            return isVacationStartInFuture(new Date(existingVacationPeriod?.from)) ? cancelVacationButtonProps() : undefined;
        }
    };

    const logOutButton = (): INavigationItemProps => ({
        url: '#',
        label: labelStore.translate('user-menu.logout'),
        onClick: (): void => {
            sessionStore.logout();
            userStore.clear();
            router.goTo(routes.dashboard);
        },
        icon: 'shut-down',
    });

    const employeeFullName = userStore.payload!.fullName.firstName + ' ' + userStore.payload!.fullName.lastName;

    const sideMenuProps = (): ISidemenuProps => ({
        user: {
            title: employeeFullName,
            isAdminOrManager: userStore.payload!.isAdmin ? labelStore.translate('company.people.rights.admin') : userStore.payload!.isManager ? labelStore.translate('company.people.rights.manager') : undefined,
            businessUnitName: userStore.payload!.businessUnitName,
            subtitle: (!userStore.payload!.isAdmin) && userStore.payload!.partnerForBusinessUnits?.length ? labelStore.translate('menu.partner-managed-units') : undefined,
            managedBusinessUnits: (!userStore.payload!.isAdmin) && userStore.payload!.partnerForBusinessUnits?.length ? businessUnitList() : undefined,
        },
        navigation: {
            items: Capacitor.getPlatform() !== 'web' ? navigationMenuItems : navigationMenuItems.concat(logOutButton()),
        },
        toggle: {
            text: 'Toggle menu',
        },
        languages: languagesProps,
        schedule: scheduleQuestionsSendTimeProps,
        questionsFrequency: userStore.payload?.companyQuestionsFrequency === 'weekly' ? questionsFrequencyProps : undefined,
        answerContext: userStore.payload?.companyAnswerContext !== undefined && userStore.payload?.companyAnswerContext.length > 1 ? answerContextProps : undefined,
        channel: eligibleUserChannels !== undefined && eligibleUserChannels.length > 1 ? channelProps : undefined,
        isUserActive: isUserActiveProps,
        timezone: timezoneDropdownProps(userStore),
        vacationCalendar: userStore.isCalendarOpen ? vacationPlannerProps : undefined,
        futureVacationRange: futureVacationRangeProps(),
        cancelFutureVacationButton: cancelFutureVacationProps(),
        footer: <p>{labelStore.translate('user-menu.powered-by')} <b>Moticheck</b>.</p>,
    });

    const sideMenuWithLimitedNavigationProps = (): ISidemenuProps => ({
        user: {
            title: employeeFullName,
            isAdminOrManager: userStore.payload!.isAdmin ? labelStore.translate('company.people.rights.admin') : userStore.payload!.isManager ? labelStore.translate('company.people.rights.manager') : undefined,
            businessUnitName: userStore.payload!.businessUnitName,
            subtitle: (!userStore.payload!.isAdmin) && userStore.payload!.partnerForBusinessUnits?.length ? labelStore.translate('menu.partner-managed-units') : undefined,
            managedBusinessUnits: (!userStore.payload!.isAdmin) && userStore.payload!.partnerForBusinessUnits?.length ? businessUnitList() : undefined,
        },
        navigation: {
            items: [logOutButton()],
        },
        toggle: {
            text: 'Toggle menu',
        },
        languages: languagesProps,
        schedule: scheduleQuestionsSendTimeProps,
        questionsFrequency: userStore.payload?.companyQuestionsFrequency === 'weekly' ? questionsFrequencyProps : undefined,
        answerContext: userStore.payload?.companyAnswerContext !== undefined && userStore.payload?.companyAnswerContext.length > 1 ? answerContextProps : undefined,
        channel: eligibleUserChannels !== undefined && eligibleUserChannels.length > 1 ? channelProps : undefined,
        isUserActive: isUserActiveProps,
        timezone: timezoneDropdownProps(userStore),
        vacationCalendar: userStore.isCalendarOpen ? vacationPlannerProps : undefined,
        futureVacationRange: futureVacationRangeProps(),
        cancelFutureVacationButton: cancelFutureVacationProps(),
        footer: <p>{labelStore.translate('user-menu.powered-by')} <b>Moticheck</b>.</p>,
    });
    const businessUnitList = (): IPageSelectProps => {
        const businessUnits = userStore.payload?.businessUnits ?? [];
        const partnerForBusinessUnits = userStore.payload?.partnerForBusinessUnits ?? [];
        const partnerIdsSet = new Set(partnerForBusinessUnits);
        const filteredBusinessUnits = businessUnits.filter((bu) =>
            partnerIdsSet.has(bu.id.toString()),
        );

        const options = filteredBusinessUnits.map((bu) => ({
            label: bu.name,
            value: bu.name,
        }));

        return {
            label: labelStore.translate('user-menu.partner-managed-units'),
            id: 'dashboard-managed-units',
            value: filteredBusinessUnits.length > 0 ? filteredBusinessUnits[0].name : '',
            options,
        } as IPageSelectProps;
    };

    return (
        <Header
            logo={{
                url: '#',
                image: logo,
                title: 'Moticheck',
            }}
            navigation={{
                items: navigationMenuItems,
            }}
            sideMenu={sideMenuProps()}
            sideMenuWithLimitedNavigation={sideMenuWithLimitedNavigationProps()}
        />
    );
});
